import { useEffect, useState } from "react";
import axios from "axios";
import { Box, Grid, Paper, Skeleton, Tooltip, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import InboxIcon from "@mui/icons-material/Inbox";
import MessageIcon from "@mui/icons-material/Message";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import TodayIcon from "@mui/icons-material/Today";
import ForumIcon from "@mui/icons-material/Forum";

import { hostname } from "../../utils";
import { DEMO_MODE_KEY } from "../../constants";
import {
  DEMO_INBOX_METRICS,
  generateDemoInboxTimeseries,
} from "../../constants/demoData";

const CompactMetric = ({
  icon,
  title,
  value,
  tooltip,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  tooltip: string;
}) => (
  <Tooltip title={tooltip} arrow placement="top">
    <Paper elevation={1} sx={{ px: 1.5, py: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
        <Box sx={{ color: "text.secondary", display: "flex", "& svg": { fontSize: 16 } }}>
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ lineHeight: 1.1, fontWeight: 600 }}>
        {value}
      </Typography>
    </Paper>
  </Tooltip>
);

interface InboxMetrics {
  totalInboxes: number;
  totalMessages: number;
  recentMessages: number;
  uniqueSenders: number;
  messagesToday: number;
  activeThreads: number;
}

interface TimeseriesSeries {
  id?: string;
  inboxId?: string;
  name: string;
  label: string;
  data: number[];
}

interface TimeseriesResponse {
  dates: string[];
  series: TimeseriesSeries[];
}

interface Props {
  companyId: string;
  getToken: () => Promise<string>;
  showMetrics?: boolean;
  groupBy?: "inbox" | "type";
}

const formatNumber = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

// Matches the sequence/content metrics palette so the three charts feel like a set.
const SEQUENCE_PALETTE = ["#1976d2", "#0288d1", "#00c853", "#ff9800"];

const shortenName = (name: string): string => {
  if (name.length <= 32) return name;
  return name.slice(0, 30).trimEnd() + "…";
};

const formatHourTick = (iso: string) => {
  const d = new Date(iso);
  if (d.getHours() === 0) {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return "";
};

const formatHourTooltip = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
  });
};

const normalizeStats = (raw: any): InboxMetrics | null => {
  if (!raw) return null;
  return {
    totalInboxes: raw.totalInboxes ?? raw.total_inboxes ?? 0,
    totalMessages: raw.totalMessages ?? raw.total_messages ?? 0,
    recentMessages: raw.recentMessages ?? raw.recent_messages ?? 0,
    uniqueSenders: raw.uniqueSenders ?? raw.unique_senders ?? 0,
    messagesToday: raw.messagesToday ?? raw.messages_today ?? 0,
    activeThreads: raw.activeThreads ?? raw.active_threads ?? 0,
  };
};

const InboxMetricsBar: React.FC<Props> = ({ companyId, getToken, showMetrics = true, groupBy = "type" }) => {
  const [metrics, setMetrics] = useState<InboxMetrics | null>(null);
  const [timeseries, setTimeseries] = useState<TimeseriesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(
    () => localStorage.getItem(DEMO_MODE_KEY) === "true"
  );

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DEMO_MODE_KEY) {
        setIsDemoMode(e.newValue === "true");
      }
    };
    const checkDemoMode = () => {
      const currentValue = localStorage.getItem(DEMO_MODE_KEY) === "true";
      setIsDemoMode((prev) => (prev !== currentValue ? currentValue : prev));
    };
    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(checkDemoMode, 500);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (isDemoMode) {
      setLoading(true);
      const timer = setTimeout(() => {
        if (!cancelled) {
          setMetrics(DEMO_INBOX_METRICS);
          setTimeseries(generateDemoInboxTimeseries(groupBy));
          setLoading(false);
        }
      }, 300);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }

    const load = async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const [statsRes, seriesRes] = await Promise.all([
          axios.get(`${hostname}/inboxes/statistics`, {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${hostname}/inboxes/timeseries`, {
            params: { companyId, days: 7, groupBy },
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!cancelled) {
          setMetrics(normalizeStats(statsRes.data));
          setTimeseries(seriesRes.data ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setMetrics(null);
          setTimeseries(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, getToken, isDemoMode, groupBy]);

  if (loading) {
    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        {showMetrics && (
          <Grid container spacing={1} sx={{ mb: 1 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <Skeleton variant="rounded" height={56} />
              </Grid>
            ))}
          </Grid>
        )}
        <Skeleton variant="rounded" height={160} />
      </Box>
    );
  }

  if (!metrics || metrics.totalMessages === 0) {
    return null;
  }

  const chartSeries =
    timeseries?.series.map((s, i) => ({
      label: shortenName(s.label || s.name),
      data: s.data,
      color: SEQUENCE_PALETTE[i % SEQUENCE_PALETTE.length],
      stack: "messages",
      area: true,
      showMark: false,
      curve: "monotoneX" as const,
    })) ?? [];

  const chartDates = timeseries?.dates ?? [];
  const hasChartData = chartSeries.some((s) => s.data.some((v) => v > 0));

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {showMetrics && (
        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<InboxIcon />}
              title="Inboxes"
              value={formatNumber(metrics.totalInboxes)}
              tooltip="Total connected inboxes"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<MessageIcon />}
              title="Messages"
              value={formatNumber(metrics.totalMessages)}
              tooltip="Total messages across all inboxes"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<MessageIcon />}
              title="Last 30d"
              value={formatNumber(metrics.recentMessages)}
              tooltip="Messages in the last 30 days"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<PeopleAltIcon />}
              title="Senders"
              value={formatNumber(metrics.uniqueSenders)}
              tooltip="Unique senders in the last 30 days"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<TodayIcon />}
              title="Today"
              value={formatNumber(metrics.messagesToday)}
              tooltip="Messages received today"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<ForumIcon />}
              title="Threads"
              value={formatNumber(metrics.activeThreads)}
              tooltip="Active reply threads"
            />
          </Grid>
        </Grid>
      )}
      <Paper elevation={1} sx={{ px: 1.5, py: 1 }}>
        {hasChartData ? (
          <Box sx={{ width: "100%", height: 160 }}>
            <LineChart
              xAxis={[
                {
                  scaleType: "point",
                  data: chartDates,
                  valueFormatter: (value: string, context: any) =>
                    context?.location === "tooltip"
                      ? formatHourTooltip(value)
                      : formatHourTick(value),
                  tickLabelInterval: (value: string) => {
                    const d = new Date(value);
                    return d.getHours() === 0;
                  },
                },
              ]}
              yAxis={[{ disableLine: true, disableTicks: true }]}
              series={chartSeries}
              height={160}
              margin={{ top: 10, right: 10, bottom: 24, left: 10 }}
              slotProps={{ legend: { hidden: true } }}
              sx={{
                "& .MuiAreaElement-root": { fillOpacity: 0.35 },
                "& .MuiChartsAxis-tickLabel": { fontSize: "0.7rem" },
                "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel": { display: "none" },
              }}
            />
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 1, textAlign: "center" }}>
            No messages in the last 7 days yet.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default InboxMetricsBar;
